// Which attachments/artifacts open in the in-app document viewer, and where their body comes from. ONE
// predicate, three surfaces — this file is the web third of a parity contract with iOS
// `DocumentClassifier` and Android `DocumentKind`; `lib/doc-viewer.test.ts`,
// `SymphonyTests/DocumentKindTests.swift` and `features/DocumentKindTest.kt` carry the SAME cases in the
// SAME order. Change one and you change all three.
//
// ⚠️ TRACKED DIVERGENCE — `pdf` / `audio` / `doc` / `slides` are WEB-ONLY. (`csv` and `text` WERE, and
// are now on all three: both natives classify them and render a table / a monospaced column.)
//
// The rows marked `WEB-ONLY` below (in `KIND_BY_EXTENSION` and `KIND_BY_MIME`) exist on web and NOT on
// either native surface, so for those inputs the three surfaces deliberately disagree. Everything else in
// this file — the ordered steps, every helper, every other row — remains byte-for-byte shared, and the 8-row
// `affordanceCases` table the two native test files share is untouched.
//
// It is a divergence rather than an exception (a phone can render a PDF; this is deferred effort), and it
// is deliberate rather than an oversight, because adding the kinds to the SHARED classifier before native
// has bodies for them is a REGRESSION on native, not a no-op:
//   - Android's `DocumentViewer.kt` body switch has an `else -> Unit` and `isTextLoadKind()` would be false,
//     so it compiles clean and renders a BLANK SCREEN — while `openAffordance` flips the hand-off glyph to
//     an eye, i.e. the affordance starts lying. That is exactly the defect the affordance work fixed.
//   - iOS's `kindLabel` / `documentBody` / `reload` are exhaustive switches with no `default`, so the BUILD
//     breaks in three places.
//   - Today a `.pdf` on iOS opens QuickLook and on Android an ACTION_VIEW chooser, both of which render
//     PDFs well. Shipping the shared change would make native strictly worse.
// That is why `csv`/`text` could be shared and `pdf` could not: the text-shaped kinds ride the load path
// both natives already have, while a PDF needs a binary one neither has yet.
// Porting it means real work on both (a binary load path — both platforms' loaders return a `String` — plus
// PDFKit / `PdfRenderer`, and `documentViewer.kind.*` across 37 locales × 2 platforms). See the note in
// `packages/orion-web/CLAUDE.md`.
//
// NOTE FOR WHOEVER OPENS THE NATIVE FILES: the two native classifiers now carry the `csv`/`text` rows too,
// so the remaining divergence is `pdf`/`audio`/`doc`/`slides` alone.
//
// Do NOT "preserve parity" by layering a web-only classifier over this one
// (`docViewerKind(...) ?? webOnlyKind(...)`): step 1 is TERMINAL, so a `null` returned from a *stop* is
// indistinguishable from a `null` that *fell through*, and `??` would silently break the terminal rule for
// exactly the input this contract pins hardest (`notes.pdf` carrying a `.md` URL). One classifier, one
// place, the extra rows marked.
//
// PARITY CONTRACT — the steps are ORDERED and TERMINAL at the first step that yields an answer:
//
//   0. Wixel asset — header-driven, ahead of everything else. A wixel asset's file name carries no
//      meaningful extension for the steps below to key off. Requires the header's `wixelAssetId`
//      present (trimmed, non-empty) AND at least one of `wixelViewerUrl`/`wixelEditorUrl`/
//      `wixelPreviewUrl` also present — not `wixelAssetId` alone, since the header is unvalidated
//      freeform JSON and a stray/partial field with no real URLs must not classify as a renderable
//      wixel file.
//   1. Name extension — authoritative, and terminal EVEN ON A MISS. `ext` = after the last `.` of the
//      trimmed name, lowercased. Non-empty → `md`/`markdown` = markdown, `html`/`htm` = html, a known
//      image/video extension (`mediaExtensionKind`, `lib/artifact-image.ts` — reused, not duplicated)
//      = `image`/`video`, anything else = null and we STOP (so `notes.pdf` pointing at a `.md` URL, or
//      at an image URL, stays null).
//   2. URL extension — only when step 1 found no extension at all. `firstHttpsUrl` (the FileRecord
//      header's `rawUrl`, else the first CASE-SENSITIVE `https://\S+` run — see its own note on why
//      `extractUrl` is not reused), run through `urlPathExtension`. A match answers; a
//      non-doc extension is NOT an answer (it falls through to the mime rescue below), which is what
//      keeps `docViewerKind(name, content) === 'markdown'` identical to native `isMarkdownFile` when
//      no mime is supplied.
//   3. MIME — RESCUE ONLY, never a veto. Lowercased, cut at `;`. `text/markdown`/`text/x-markdown` →
//      markdown; `text/html`/`application/xhtml+xml` → html. `text/plain`, `application/octet-stream`
//      and empty → null (a miss, not a veto — step 4 still runs).
//
//      ⚠️ `text/plain` MUST stay a miss, and this is the most dangerous available edit to this file. Now
//      that a `text` KIND exists it looks like the obvious wiring, and it is a trap: WhatsApp ingestion
//      normalises every `text/*` → `text/plain`, and our own Android mime map answers
//      `application/octet-stream` for `.html`, so in our pipelines `text/plain` is the word for UNKNOWN,
//      not for "plain text". Mapping it would make step 3 answer for every extension-less unknown
//      attachment and STOP STEP 4 RUNNING — a real image would render as a raw text dump, and a real
//      report as its own source. Pinned on all three surfaces, plus the `text/plain` → `image` case in
//      `doc-viewer.test.ts` that exists specifically to catch this.
//   4. Image / video, content-URL-driven — the LAST fallback, only reached when the name carried no
//      extension AND steps 2-3 answered nothing. Via the SAME `imageUrl`/`videoUrl` helpers
//      (`lib/artifact-image.ts`) the existing image-preview logic already uses, so the classifier and
//      that preview can never disagree about the same content. This has to sit here rather than
//      earlier: an explicit MIME (step 3) must still be able to rescue an untitled file whose content
//      merely happens to embed a non-doc-shaped URL (a pinned case: `docViewerKind('report',
//      'https://x.com/pic.png', 'text/html')` answers `html`, not `image` — the caller-supplied mime is
//      more authoritative than an incidental URL extension when the name itself says nothing). A real
//      image/video artifact almost always ALSO has a real name extension anyway, which step 1 already
//      catches; step 4 only matters for an untitled one (no name ext, no md/html URL ext, no mime).
//   5. Otherwise null.
//
// Why mime out-ranks step 4's image/video fallback (but never a name extension): our own Android mime
// map answers `application/octet-stream` for `.html` (the bug this feature came from), and
// WhatsApp-ingested attachments normalise `text/*` → `text/plain`, so a real `.md` arrives labelled
// plain — the NAME has to win over mime. But nothing analogous forces image/video ahead of an explicit
// mime the same way, so mime keeps its established rescue priority there. Deliberately NOT ported:
// Android `FileTypeKind.resolve`'s `ext.length <= 8` guard — iOS's classifier has none, and adding it on
// one surface only would break the contract.

import { imageUrl, mediaExtensionKind, videoUrl } from './artifact-image';
import { parseFileRecordContent } from './file-record';

export type DocViewerKind =
  | 'markdown'
  | 'html'
  | 'image'
  | 'video'
  | 'wixel'
  | 'pdf'
  | 'csv'
  | 'text'
  | 'audio'
  | 'doc'
  | 'slides'
  | 'sheet';

/** Trimmed non-empty — the same "present" test the wixel step-0 check and the header-field rules use. */
function isNonEmpty(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

/** The lowercased extension of a file NAME: after the last `.` of the trimmed name, `''` when the name
 *  has none (`notes`) or ends in the dot (`trailing.`). Mirrors native `nameExtension`. */
export function nameExtension(name: string): string {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  return dot >= 0 && dot < trimmed.length - 1 ? trimmed.slice(dot + 1).toLowerCase() : '';
}

/** The lowercased path extension of a URL STRING, robust to query/fragment: strip a trailing
 *  `#fragment`, then a `?query`, take the last `/`-delimited segment, then the substring after its last
 *  `.` (`''` when none). Pure string ops — deliberately NOT `new URL(...).pathname`, so a signed link
 *  like `…/file.md?token=…` classifies correctly and the result matches native byte-for-byte. */
export function urlPathExtension(urlString: string): string {
  let s = urlString;
  const hash = s.indexOf('#');
  if (hash >= 0) s = s.slice(0, hash);
  const query = s.indexOf('?');
  if (query >= 0) s = s.slice(0, query);
  const slash = s.lastIndexOf('/');
  if (slash >= 0) s = s.slice(slash + 1);
  const dot = s.lastIndexOf('.');
  return dot >= 0 && dot < s.length - 1 ? s.slice(dot + 1).toLowerCase() : '';
}

/** True when `content` is a bare https URL (a POINTER to the document) rather than an inline document
 *  body — trimmed, it starts with `https://` (case-insensitive) and contains no whitespace anywhere.
 *  Mirrors native `isBareHttpsUrl`. */
export function isBareHttpsUrl(content: string): boolean {
  const t = content.trim();
  return t.toLowerCase().startsWith('https://') && !/\s/.test(t);
}

/** The document's URL as NATIVE sees it: the FileRecord header's `rawUrl` when present, else the first
 *  `https://…` run in the body.
 *
 *  `extractUrl` from `lib/artifact-image.ts` is deliberately NOT reused here, even though it looks like
 *  the same function: it matches `http(s)` CASE-INSENSITIVELY and strips trailing markdown punctuation,
 *  where iOS `firstURLString` / Android `extractURL` match a case-SENSITIVE `https://\S+` and strip
 *  nothing. Those two divergences made web classify inputs native rejects — `http://…/q3.md` became a
 *  "markdown document" whose body was then the URL string itself, and `See https://x/r.md).` resolved to
 *  a `.md` extension web alone accepted. The classifier is a byte-for-byte parity contract, so it gets
 *  its own extractor; `extractUrl` stays as-is for the image/file-card inference paths that want it. */
export function firstHttpsUrl(content?: string | null): string | null {
  if (!content) return null;
  const { header, body } = parseFileRecordContent(content);
  // A header `rawUrl` that is empty or whitespace-only is treated as ABSENT (D2) — the same rule
  // `docSource` applies, so the classifier and the load plan never disagree about whether a header
  // carries a URL.
  if (header?.rawUrl?.trim()) return header.rawUrl.trim();
  // The natives' FIRST branch, before any regex: a single-line body that STARTS with `https://` is the
  // URL in its entirety (iOS `firstURLString`: `if !content.contains("\n") && content.hasPrefix("https://")`,
  // Android `extractURL`). It is not an optimisation — it changes the answer, because the whole body may
  // carry trailing text the regex would have cut off. `https://x/report.md (draft)` is the whole URL to
  // native, whose last path segment is `report.md (draft)` and whose extension is therefore `md (draft)`
  // (not a document); the regex alone answered `https://x/report.md` and web called it markdown.
  if (!body.includes('\n') && body.startsWith('https://')) return body;
  const match = body.match(/https:\/\/\S+/);
  return match ? match[0] : null;
}

// Step 1's table — and step 2 reads the SAME one, so a row added here classifies off a signed URL's path
// extension for free. Adding a second map is how the two steps drift.
const KIND_BY_EXTENSION: Record<string, DocViewerKind> = {
  md: 'markdown',
  markdown: 'markdown',
  html: 'html',
  htm: 'html',
  // The `text` set is deliberately SMALL: each entry is a case in three test tables and a promise all
  // three platforms owe forever. `csv`/`text` are shared with both natives; `pdf` is still WEB-ONLY.
  //   - `.ts` is REJECTED, and not on taste: `.ts` is also MPEG transport stream, so claiming it would
  //     classify a video file as text on web alone.
  //   - `.py` / `.sh` / `.rb` / … are REJECTED: the set is open-ended (take one and you owe the rest), and
  //     a code view with no syntax highlighting reads worse than the download it replaces.
  pdf: 'pdf',
  csv: 'csv',
  tsv: 'csv',
  txt: 'text',
  log: 'text',
  json: 'text',
  yaml: 'text',
  yml: 'text',
  // Audio. `.webm` and `.mp4` are deliberately NOT here — they are already video extensions, and step 1 is
  // terminal so one name cannot be both. A `<video>` element plays an audio-only file fine (it just shows no
  // picture), and in chat an `audio/*` attachment is routed to the player BEFORE the classifier runs, so a
  // voice note recorded as `.webm` still gets a player. Claiming them here would break real video instead.
  mp3: 'audio',
  m4a: 'audio',
  aac: 'audio',
  wav: 'audio',
  ogg: 'audio',
  oga: 'audio',
  opus: 'audio',
  flac: 'audio',
  // Office, converted server-side (`pages/api/convert-document.ts`) to markdown. Deliberately NOT here:
  //   - `.xls` (pre-2007) is still NOT claimed: it is an OLE compound file, not a zip, so nothing here reads
  //     it. `.xlsx` IS claimed — it is an OOXML package read with the same unzip as `.docx`, with no SheetJS
  //     anywhere near it (see common-lib `src/documents/SpreadsheetHtml.ts`).
  //   - `.doc`/`.ppt` — pre-2007 binary, not OOXML packages, and nothing here reads them.
  docx: 'doc',
  pptx: 'slides',
  xlsx: 'sheet',
};

const KIND_BY_MIME: Record<string, DocViewerKind> = {
  'text/markdown': 'markdown',
  'text/x-markdown': 'markdown',
  'text/html': 'html',
  'application/xhtml+xml': 'html',
  // `text/csv` and `application/json` are shared with both natives; `application/pdf` is still WEB-ONLY.
  // These three are safe to rescue precisely because nothing in our pipelines normalises TO them — unlike `text/plain`, which must NOT be added here at any price: see the
  // STEP 3 note above. `application/pdf` is the highest-value row in this table, because an ingested
  // attachment with no extension anywhere and an explicit pdf mime is the exact shape step 3 exists for.
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'text/tab-separated-values': 'csv',
  'application/json': 'text',
  // Audio, by mime — the rescue that matters for a voice note whose name carries no extension at all.
  'audio/mpeg': 'audio',
  'audio/mp4': 'audio',
  'audio/aac': 'audio',
  'audio/wav': 'audio',
  'audio/x-wav': 'audio',
  'audio/ogg': 'audio',
  'audio/webm': 'audio',
  'audio/flac': 'audio',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'slides',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'sheet',
};

/** Which in-app viewer renders this file, or null for "hand off to the browser as before". See the
 *  ordered contract in this file's header — the order is the contract. */
export function docViewerKind(
  /** Optional because a name is genuinely not always known — an attachment can arrive as a URL and a mime and
   *  nothing else, which is why STEP 1 below already reads `name ?? ''` and falls through to the URL and mime
   *  steps. The parameter was typed `string` while the body handled `undefined`, so every caller holding an
   *  optional name had to launder it; that is the type lying about the contract, not the callers being sloppy. */
  name: string | undefined,
  content?: string | null,
  mimeType?: string | null,
): DocViewerKind | null {
  // STEP 0 — wixel asset.
  const { header } = parseFileRecordContent(content);
  const hasWixelUrl = isNonEmpty(header?.wixelViewerUrl) || isNonEmpty(header?.wixelEditorUrl) || isNonEmpty(header?.wixelPreviewUrl);
  if (isNonEmpty(header?.wixelAssetId) && hasWixelUrl) return 'wixel';

  // STEP 1 — name extension. Terminal even on a miss: once the name HAS an extension, nothing below
  // (URL, mime, or the image/video content fallback) gets a say.
  const ext = nameExtension(name ?? '');
  if (ext) return KIND_BY_EXTENSION[ext] ?? mediaExtensionKind(ext);

  // STEP 2 — URL extension (name had none).
  if (content) {
    const url = firstHttpsUrl(content);
    if (url) {
      const urlExt = urlPathExtension(url);
      const byUrl = KIND_BY_EXTENSION[urlExt];
      if (byUrl) return byUrl;
    }
  }

  // STEP 3 — MIME rescue.
  const mime = (mimeType ?? '').toLowerCase().split(';')[0].trim();
  const byMime = mime ? KIND_BY_MIME[mime] ?? null : null;
  if (byMime) return byMime;

  // STEP 4 — image / video, content-URL-driven, the last fallback (see the header note on why this
  // sits BELOW the mime rescue rather than ahead of everything).
  if (imageUrl(content)) return 'image';
  if (videoUrl(content)) return 'video';

  return null;
}

/** Where the viewer reads the document from: a URL to download, or the body it already holds. Null when
 *  there is nothing to show ("unavailable" — the viewer renders its error state, never nothing).
 *  Mirrors native `DocumentLoadPlan`. */
export type DocSource = { kind: 'inline'; text: string } | { kind: 'remote'; url: string };

/** A header `rawUrl`, TRIMMED (D3) — `null` when the header carries none at all.
 *
 *  Trimming is the contract, not tidiness: a `rawUrl` that arrived with a stray newline or leading space is
 *  still the URL, and handing the untrimmed string to `fetch` produced a request to a URL nobody serves.
 *  A value that is empty or whitespace-only counts as ABSENT (D2) — see `docSource`. */
function headerUrl(raw?: string | null): string | null {
  return raw?.trim() || null;
}

/** True when `url` is one we will actually download (D6, all three surfaces). The download path is a
 *  cross-origin `fetch` of a token-signed media URL, so `http://` is not a request we will ever make — the
 *  signed URL and its token would travel in clear, and a mixed-content block would fail it anyway — and
 *  `file:`/`data:`/`javascript:` let an agent-authored header name something that is not a document at all. */
function isHttpsUrl(url: string): boolean {
  return url.toLowerCase().startsWith('https://');
}

/** The load plan, in the ORDER the three surfaces share:
 *   1. content trimmed non-empty → parse the header; `rawUrl` else a bare-https body is the download URL;
 *      download when there is one, else the body (trimmed non-empty) IS the document; else fall through.
 *   2. local bytes — native only; web never holds a just-picked file's bytes here.
 *   3. unavailable.
 *  Every emptiness check is on the TRIMMED string (JS `trim()` counts U+00A0 as whitespace, which is what
 *  the native twins' checks do too), so a header-only artifact or a whitespace body is "unavailable"
 *  rather than an empty document.
 *
 *  Two rules about the header's `rawUrl` that read alike and are deliberately opposite — the difference is
 *  PRESENT-BUT-UNUSABLE versus ABSENT:
 *
 *  - **Empty or whitespace-only → ABSENT (D2), fall through to the body.** The header is server-authored and
 *    an omitted value can arrive as `""`, so it means the same as no `rawUrl` key at all. Web used to fall
 *    through by accident (`''` is falsy); it is now deliberate and pinned, because the twins disagreed — iOS
 *    answered `unavailable` and Android answered `Download("")`, which then failed with no diagnostic.
 *  - **Present but not `https` → UNAVAILABLE (D6), never the body.** A present `rawUrl` states where the
 *    document lives; if we can't fetch it we have no document, and no retry will change that. Rendering the
 *    body instead would silently substitute different content for the document the header named — web did
 *    exactly that while iOS refused and Android raised a *retryable* error, so one input had three answers. */
export function docSource(content?: string | null): DocSource | null {
  if (!content?.trim()) return null;
  const { header, body } = parseFileRecordContent(content);
  const fromHeader = headerUrl(header?.rawUrl);
  if (fromHeader) return isHttpsUrl(fromHeader) ? { kind: 'remote', url: fromHeader } : null;
  const url = isBareHttpsUrl(body) ? body.trim() : null;
  if (url) return { kind: 'remote', url };
  // The body IS the document — returned unchanged and UNTRIMMED (leading whitespace can be meaningful
  // markdown, and an html document's own leading newline is its author's).
  if (body.trim()) return { kind: 'inline', text: body };
  return null;
}
